/**
 * People block — accessible modal.
 *
 * Opens a modal dialog when a person card trigger is activated.
 * Traps focus, handles ESC key, and returns focus on close.
 * Fade + scale transition on open/close.
 */

const FOCUSABLE = 'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

function getFocusable(container) {
  return Array.from(container.querySelectorAll(FOCUSABLE)).filter(
    (el) => !el.closest('[hidden]') || el.closest('[hidden]') === container
  );
}

function openModal(trigger, modal) {
  // Lock body scroll
  document.documentElement.style.overflow = 'hidden';

  // Show the modal (display: flex via .is-open)
  modal.removeAttribute('hidden');
  modal.classList.add('is-open');

  // Trigger transition on next frame
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      modal.classList.add('is-visible');
    });
  });

  trigger.setAttribute('aria-expanded', 'true');

  // Focus the close button (first focusable in modal)
  const focusable = getFocusable(modal);
  if (focusable.length) {
    focusable[0].focus();
  }
}

function closeModal(trigger, modal) {
  modal.classList.remove('is-visible');

  const inner = modal.querySelector('.person-modal-inner');
  const onEnd = () => {
    inner.removeEventListener('transitionend', onEnd);
    modal.classList.remove('is-open');
    modal.setAttribute('hidden', '');
    document.documentElement.style.overflow = '';
    trigger.setAttribute('aria-expanded', 'false');
    trigger.focus();
  };

  // If reduced motion or no transition, close immediately
  const duration = parseFloat(getComputedStyle(inner).transitionDuration);
  if (!duration) {
    onEnd();
  } else {
    inner.addEventListener('transitionend', onEnd, { once: true });
    // Safety fallback in case transitionend doesn't fire
    setTimeout(onEnd, duration * 1000 + 50);
  }
}

function trapFocus(modal, e) {
  const focusable = getFocusable(modal);
  if (!focusable.length) return;

  const first = focusable[0];
  const last = focusable[focusable.length - 1];

  if (e.key === 'Tab') {
    if (e.shiftKey) {
      if (document.activeElement === first) {
        last.focus();
        e.preventDefault();
      }
    } else {
      if (document.activeElement === last) {
        first.focus();
        e.preventDefault();
      }
    }
  }
}

document.querySelectorAll('.people-items').forEach((list) => {
  list.querySelectorAll('.person-trigger').forEach((trigger) => {
    const modalId = trigger.getAttribute('aria-controls');
    const modal = document.getElementById(modalId);

    if (!modal) return;

    let isOpen = false;

    trigger.addEventListener('click', () => {
      if (!isOpen) {
        isOpen = true;
        openModal(trigger, modal);
      }
    });

    const close = () => {
      if (isOpen) {
        isOpen = false;
        closeModal(trigger, modal);
      }
    };

    modal.querySelector('.person-modal-close')?.addEventListener('click', close);

    modal.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        close();
        return;
      }
      trapFocus(modal, e);
    });

    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        close();
      }
    });
  });
});
